import type { ReactNode } from "react";
import { cx } from "@/utils/cx";
import { Badge } from "@/components/base/badges/badges";
import type { SseConnectionState } from "../../lib/sseClient";
import type {
  SyncProgressSnapshot,
  SyncStateZoneSnapshot,
} from "../../hooks/useSyncProgressSse";

/**
 * UC-001 SyncStatusBanner (UX-2) — persistentní topbar indikátor sync stavu.
 *
 * Per princip #4 (sync transparency, REQ-UI-024):
 *   - Admin VŽDY vidí globální stav sync (running / ok / partial / failed / never).
 *   - Připojení SSE klienta zobrazené jako "Probíhá retry…" pokud disconnect.
 *   - Color-not-only — text + ikona + barva (Badge variant).
 *
 * Renderuje se v admin layoutu topbaru (admin shell vždy nahoře).
 */

export interface SyncStatusBannerProps {
  snapshot: SyncProgressSnapshot;
  zones: SyncStateZoneSnapshot[];
  className?: string;
}

interface Aggregate {
  label: string;
  detail: string;
  color: "gray" | "blue" | "success" | "warning" | "error";
  symbol: string;
}

function pickWorstState(states: string[]): string {
  if (states.includes("selhal")) return "selhal";
  if (states.includes("castecny")) return "castecny";
  if (states.includes("running")) return "running";
  if (states.includes("never") || states.length === 0) return "never";
  return "kompletni";
}

function badgeForState(state: string): Pick<Aggregate, "label" | "color" | "symbol"> {
  switch (state) {
    case "running":
      return { label: "Synchronizace běží", color: "blue", symbol: "↻" };
    case "kompletni":
      return { label: "Sync OK", color: "success", symbol: "✓" };
    case "castecny":
      return { label: "Sync částečný", color: "warning", symbol: "!" };
    case "selhal":
      return { label: "Sync selhal", color: "error", symbol: "✕" };
    case "never":
    default:
      return { label: "Bez sync", color: "gray", symbol: "•" };
  }
}

function describeConnection(state: SseConnectionState): string | null {
  switch (state) {
    case "retrying":
      return "Probíhá retry…";
    case "closed":
      return "Stream uzavřen";
    case "connecting":
      return "Připojuji…";
    default:
      return null;
  }
}

function aggregate(snapshot: SyncProgressSnapshot, zones: SyncStateZoneSnapshot[]): Aggregate {
  const liveStates = Object.values(snapshot.byZone).map((e) => e.state);
  const persistedStates = zones.map((z) => z.last_state);
  const combined = liveStates.length > 0 ? liveStates : persistedStates;
  const worst = pickWorstState(combined);
  const meta = badgeForState(worst);
  const liveZoneCount = Object.keys(snapshot.byZone).length;
  const persistedZoneCount = zones.length;
  const detail =
    worst === "never"
      ? "Žádný sync zatím neproběhl."
      : `${liveZoneCount > 0 ? liveZoneCount : persistedZoneCount} zóny`;
  return { ...meta, detail };
}

export function SyncStatusBanner({
  snapshot,
  zones,
  className,
}: SyncStatusBannerProps): ReactNode {
  const agg = aggregate(snapshot, zones);
  const connectionMessage = describeConnection(snapshot.connection);

  return (
    <div
      className={cx(
        "inline-flex items-center gap-2 rounded-md bg-secondary px-2 py-1",
        className,
      )}
      data-testid="sync-status-banner"
      role="status"
      aria-live="polite"
    >
      <Badge color={agg.color}>
        <span aria-hidden="true">{agg.symbol}</span> {agg.label}
      </Badge>
      <span className="text-xs text-tertiary">{agg.detail}</span>
      {connectionMessage && (
        <span
          className="text-xs font-bold text-warning-primary"
          data-testid="sync-banner-connection"
        >
          {connectionMessage}
        </span>
      )}
    </div>
  );
}
